import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, Copy, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import AccountSetupModal from '../components/auth/AccountSetupModal';


const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useAuth();
  const [paymentData, setPaymentData] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [accountSetupComplete, setAccountSetupComplete] = useState(false);
  const [showAccountSetup, setShowAccountSetup] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');

  useEffect(() => {
    if (location.state?.payment) {
      setPaymentData(location.state.payment);
    }
    if (location.state?.booking) {
      setBookingData(location.state.booking);
    }

    // Auto-login when backend created credentials during payment
    const authToken = location.state?.booking?.auth_token;
    const bookingUser = location.state?.booking?.user;
    if (authToken && bookingUser) {
      const userData = { ...bookingUser, userType: 'user' };
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('lastActivityTimestamp', Date.now().toString());
      if (updateUser) updateUser(userData);
    }

    if (!location.state?.payment) {
      navigate(user?.userType === 'admin' ? '/admin' : `/${user?.userType || 'user'}/dashboard`, { replace: true });
    }
  }, [location.state, navigate, user, updateUser]);

  // Show processing message and delay account setup modal for better UX
  useEffect(() => {
    if (bookingData?.needs_account_setup && !accountSetupComplete) {
      setProcessingMessage('Processing your booking...');

      // Show processing for 1.5 seconds before showing account setup modal
      const timer = setTimeout(() => {
        setProcessingMessage('');
        setShowAccountSetup(true);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [bookingData, accountSetupComplete]);

  // Auto-redirect countdown (only if account setup is not needed or completed)
  useEffect(() => {
    if (!paymentData || (bookingData?.needs_account_setup && !accountSetupComplete)) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleGoToDashboard();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [paymentData, bookingData, accountSetupComplete]);

  const handleCopyPaymentId = () => {
    if (paymentData?.razorpay_payment_id) {
      navigator.clipboard.writeText(paymentData.razorpay_payment_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleGoToDashboard = () => {
    const dashboardPath = user?.userType === 'admin' 
      ? '/admin' 
      : `/${user?.userType || 'user'}/dashboard`;
    navigate(dashboardPath, { replace: true });
  };

  const handleAccountSetupSuccess = () => {
    setAccountSetupComplete(true);
    // Redirect to client dashboard after account setup
    navigate('/user/dashboard', { replace: true });
  };

  if (!paymentData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg-primary">
        <div className="text-center">
          <p className="text-gray-600 dark:text-dark-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  const currencySymbol = paymentData.currency === 'INR' ? '₹' : 
                         paymentData.currency === 'USD' ? '$' : 
                         paymentData.currency === 'EUR' ? '€' : 
                         paymentData.currency || '₹';

  // Determine username: email > whatsapp_number > contact
  const username = bookingData?.user?.email || bookingData?.user?.whatsapp_number || bookingData?.user?.contact || '';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-bg-primary px-4 py-8">
      <div className="max-w-md w-full bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl p-8 text-center">
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="absolute inset-0 bg-green-100 dark:bg-green-900 rounded-full animate-ping opacity-75"></div>
            <CheckCircle className="h-20 w-20 text-green-500 dark:text-green-400 relative z-10" />
          </div>
        </div>

        {/* Success Message */}
        <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-2">
          Payment Successful!
        </h1>
        <p className="text-gray-600 dark:text-dark-text-secondary mb-6">
          Your payment has been processed successfully.
        </p>

        {/* Payment Details Card */}
        <div className="bg-gray-50 dark:bg-dark-bg-primary rounded-lg p-6 mb-6 text-left">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
            Payment Details
          </h2>
          
          <div className="space-y-3">
            {/* Amount */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Amount Paid:</span>
              <span className="text-lg font-bold text-gray-900 dark:text-dark-text-primary">
                {currencySymbol}{parseFloat(paymentData.amount).toFixed(2)}
              </span>
            </div>

            {/* Payment Status */}
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Status:</span>
              <span className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300 rounded-full text-sm font-medium capitalize">
                {paymentData.status}
              </span>
            </div>

            {/* Razorpay Payment ID */}
            <div className="pt-3 border-t border-gray-200 dark:border-dark-border">
              <div className="flex justify-between items-start gap-2">
                <div className="flex-1">
                  <span className="text-sm text-gray-600 dark:text-dark-text-secondary block mb-1">
                    Razorpay Payment ID:
                  </span>
                  <code className="text-sm font-mono text-gray-900 dark:text-dark-text-primary break-all">
                    {paymentData.razorpay_payment_id || 'N/A'}
                  </code>
                </div>
                {paymentData.razorpay_payment_id && (
                  <button
                    onClick={handleCopyPaymentId}
                    className="flex-shrink-0 p-2 text-gray-600 dark:text-dark-text-secondary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-dark-bg-tertiary rounded-md transition-colors"
                    title="Copy Payment ID"
                  >
                    {copied ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <Copy className="h-5 w-5" />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <button
          onClick={handleGoToDashboard}
          className="w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-md transition-colors flex items-center justify-center gap-2"
        >
          Go to Dashboard
          <ArrowRight className="h-5 w-5" />
        </button>

        {/* Auto-redirect notice */}
        {!bookingData?.needs_account_setup && (
          <p className="text-sm text-gray-500 dark:text-dark-text-tertiary mt-4">
            Redirecting to dashboard in {countdown} seconds...
          </p>
        )}
      </div>

      {/* Processing Overlay - Show while preparing account setup */}
      {processingMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl p-8 text-center max-w-sm mx-4">
            <div className="flex justify-center mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
            <p className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-2">
              {processingMessage}
            </p>
            <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
              Please wait a moment...
            </p>
          </div>
        </div>
      )}

      {/* Account Setup Modal - Show after processing */}
      {showAccountSetup && !accountSetupComplete && bookingData?.setup_token && (
        <AccountSetupModal
          setupToken={bookingData.setup_token}
          userId={bookingData.user_id}
          username={username}
          onSuccess={handleAccountSetupSuccess}
        />
      )}
    </div>
  );
};

export default PaymentSuccess;
